import _ from "lodash";
import { DateTime } from "luxon";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { Placeholder } from "rsuite";
import {
  languages,
  StyleTypeContext,
  TranslationContext,
} from "../../../umbilical_tools";
import { Price } from "../../common/Price";
import PropTypes from "prop-types";
import jssVariables from "../../../jss_setup";
import { CustomCheckbox } from "../../forms/inputGroup/helpers";

const cancelationPolicyStyles = createUseStyles({
  cxls: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    fontSize: "smaller",
    maxWidth: "fit-content",
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.cxls", {}),
  }),
  terms: (props) => ({
    fontSize: "smaller",
    ..._.get(props, "customStyles.terms", {}),
  }),
  free: (props) => ({
    color: "green",
    textWrap: "wrap",
    ..._.get(props, "customStyles.free", {}),
  }),
  chargeable: (props) => ({
    color: "red",
    textWrap: "wrap",
    ..._.get(props, "customStyles.chargeable", {}),
  }),
  cxlDisclaimer: (props) => ({
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.cxlDisclaimer", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    cxls: (props) => ({
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.cxls.smallscreen", {}),
    }),
  },
});
export const CancelationPolicy = ({ prebookUpdateData }) => {
  const classes = cancelationPolicyStyles();
  const { t } = useContext(TranslationContext);

  return (
    <div className={classes.cxls}>
      {_.get(prebookUpdateData, "cancellation_policies", []).map((cxl, idx) => {
        if (cxl.frame_type === "non-refundable") {
          return (
            <span className={classes.chargeable} key={idx}>
              {t("thisBookingIsNonNefundable")}.
            </span>
          );
        }
        return (
          <span
            className={
              cxl.type === "refundable" ? classes.free : classes.chargeable
            }
            key={idx}>
            {cxl.type === "refundable"
              ? `Up ${t("to")} `
              : `${t("from")}
                  ${DateTime.fromISO(cxl.from_).toLocaleString(
                    DateTime.DATETIME_SHORT
                  )} ${t("to")} `}
            {DateTime.fromISO(cxl.to).toLocaleString(DateTime.DATETIME_SHORT)}{" "}
            {t("cancellationIs")}{" "}
            {cxl.type === "refundable"
              ? t("free")
              : `${t("chargeableWithFeeOf")} `}{" "}
            <Price styleless={true} price={cxl.fee} currency={cxl.currency} />
          </span>
        );
      })}
    </div>
  );
};
CancelationPolicy.propTypes = {
  prebookUpdateData: PropTypes.object,
};

const styles = createUseStyles({
  prebook: (props) => ({
    color: jssVariables.colors.text.dark,
    overflow: "auto",
    maxHeight: "70vh",
    ..._.get(props, "customStyles.prebook", {}),
  }),
  header: (props) => ({
    display: "grid",
    fontSize: "x-large",
    fontWeight: "bold",
    paddingBottom: jssVariables.normal_gap,
    ..._.get(props, "customStyles.header", {}),
  }),
  img: (props) => ({
    height: "9rem",
    width: "80vw",
    objectFit: "cover",
    ..._.get(props, "customStyles.img", {}),
  }),
  placeholderImg: (props) => ({
    height: "12rem!important",
    width: "80vw!important",
    ..._.get(props, "customStyles.placeholderImg", {}),
  }),
  content: (props) => ({
    display: "grid",
    alignItems: "start",
    fontSize: "large",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.content", {}),
    "& img": {
      placeSelf: "center",
      borderRadius: "5px",
    },
    ..._.get(props, "customStyles.content", {}),
  }),
  text: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 3)`,
    ..._.get(props, "customStyles.text", {}),
  }),
  bottomContainer: (props) => ({
    display: "grid",
    paddingTop: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.bottomContainer", {}),
  }),
  priceContainer: (props) => ({
    display: "grid",
    justifyContent: "end",
    ..._.get(props, "customStyles.priceContainer", {}),
  }),
  meetingPoint: (props) => ({
    maxWidth: "fit-content",
    ..._.get(props, "customStyles.meetingPoint", {}),
  }),
  extraInfo: (props) => ({
    fontSize: "large",
    maxWidth: "fit-content",
    ..._.get(props, "customStyles.extraInfo", {}),
  }),
  capitalize: (props) => ({
    textTransform: "capitalize",
    ..._.get(props, "customStyles.capitalize", {}),
  }),
  productList: (props) => ({
    listStyleType: "square",
    ..._.get(props, "customStyles.productList", {}),
  }),
  customCheckboxContr: (props) => ({
    padding: [0, jssVariables.normal_gap],
    ..._.get(props, "customStyles.customCheckboxContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    prebook: (props) => ({
      maxHeight: "65vh",
      overflowX: "hidden",
      overflowY: "auto",
      ..._.get(props, "customStyles.prebook.smallscreen", {}),
    }),
    header: (props) => ({
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    content: (props) => ({
      gridGap: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.info.content", {}),
    }),
    img: (props) => ({
      height: "9rem",
      width: "30rem",
      objectFit: "contain",
      ..._.get(props, "customStyles.img.smallscreen", {}),
    }),
    meetingPoint: (props) => ({
      display: "grid",
      gridTemplateColumns: "max-content 1fr",
      gridGap: `calc(${jssVariables.normal_gap} / 2)`,
      maxWidth: "fit-content",
      ..._.get(props, "customStyles.meetingPoint.smallscreen", {}),
    }),
    placeholderImg: (props) => ({
      height: "15rem!important",
      width: "30rem!important",
      ..._.get(props, "customStyles.placeholderImg.smallscreen", {}),
    }),
  },
});
const Prebook = ({
  loading,
  optsForDisplay,
  time,
  prebookUpdateData,
  agreed,
  setAgreed,
  withUserAgreement,
}) => {
  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = cStyler({ type, brand, objName: "ActPreBook" });
  const classes = styles({ customStyles });

  const { activity } = useSelector((state) => {
    return { activity: state.ActivitySelected?.activity ?? {} };
  });

  var totalPrice = prebookUpdateData?.full_price?.value ?? 0;
  var totalPriceCurrency = prebookUpdateData?.full_price?.currency;

  if (!totalPrice) {
    totalPrice =
      _.sum(
        (optsForDisplay?.items ?? []).map(
          (item) => Number(item.quantity) * Number(item.price.value)
        )
      ) ?? 0;
    totalPriceCurrency = optsForDisplay?.items?.[0]?.price?.currency;
  }

  return (
    <div className={classes.prebook}>
      <div className={classes.header}>
        {loading ? (
          <Placeholder.Paragraph rows={2} active />
        ) : (
          <React.Fragment>{activity.title} </React.Fragment>
        )}
      </div>
      <div className={classes.content}>
        {loading ? (
          <Placeholder.Graph active className={classes.placeholderImg} />
        ) : (
          <img
            src={_.get(activity, "images.0.sm.url", "")}
            alt=""
            className={classes.img}
          />
        )}
        <div className={classes.text}>
          {loading ? (
            <Placeholder.Paragraph rows={6} active />
          ) : (
            <React.Fragment>
              <span>
                <strong>{`${t("date")}: `}</strong>
                {DateTime.fromISO(optsForDisplay.date).toLocaleString(
                  DateTime.DATE_MED_WITH_WEEKDAY
                )}
                {time && (
                  <span>
                    {" - "}
                    {time}
                  </span>
                )}
              </span>
              <span>
                <strong>{`${t("ticketsFor")}: ${
                  optsForDisplay.ticket_option
                }`}</strong>
                <ul className={classes.productList}>
                  {optsForDisplay.items.map((item, idx) => {
                    return (
                      <li key={idx}>
                        {`${item.quantity}x ${item.name}`} (
                        <Price
                          styleless={true}
                          price={Number(item.price.value)}
                          currency={item.price.currency}
                        />
                        )
                      </li>
                    );
                  })}
                </ul>
              </span>
              {activity.meeting_point_info && (
                <span className={classes.meetingPoint}>
                  <strong>{t("meetingPoint")}: </strong>
                  {activity.meeting_point_info}
                </span>
              )}
              {optsForDisplay.items && (
                <span>
                  <strong>{t("language")}: </strong>
                  {[
                    ...new Set([
                      ...optsForDisplay.items.map((item) => item.language),
                    ]),
                  ]
                    .map((lng) => languages[_.upperCase(lng)])
                    .join(", ")}
                </span>
              )}
              {activity.supplier && (
                <div className={classes.extraInfo}>
                  <strong>{t("providedBy")}: </strong>
                  {activity.supplier.supplier_name}
                </div>
              )}
              {prebookUpdateData && (
                <CancelationPolicy prebookUpdateData={prebookUpdateData} />
              )}
            </React.Fragment>
          )}
        </div>
      </div>
      <div className={classes.bottomContainer}>
        {loading ? (
          <React.Fragment>
            <Placeholder.Paragraph rows={1} active />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <div className={classes.priceContainer}>
              <Price
                price={totalPrice}
                currency={totalPriceCurrency}
                customStyles={customStyles.priceComponent}
              />
            </div>
          </React.Fragment>
        )}
      </div>
      {!loading && (
        <div className={classes.customCheckboxContr}>
          <CustomCheckbox
            id="actPrebookAgreement"
            label={t("iHaveReadAgreeWithAbove")}
            checked={agreed}
            setChecked={setAgreed}
          />
        </div>
      )}
    </div>
  );
};
Prebook.defaultProps = {
  loading: true,
  optsForDisplay: {},
  time: "",
  prebookUpdateData: null,
};
Prebook.propTypes = {
  loading: PropTypes.bool,
  optsForDisplay: PropTypes.object,
  time: PropTypes.string,
  prebookUpdateData: PropTypes.object,
  agreed: PropTypes.bool.isRequired,
  setAgreed: PropTypes.func.isRequired,
};

export default Prebook;
